import styled from "@emotion/styled";
import React, { useCallback, useMemo, useState } from "react";
import { getRidOfAggregation } from "../../../../../charts/TableView/Elements/EditableMenu";
import Input from "../../../../../UI/Form/Input/Input";
import Select from "../../../../../UI/Form/Select/Select";
import {
  aggregations,
  formulaOptions,
} from "../../../../../utils/constants/constants";
import { debounce } from "lodash-es";
import {
  getColumn,
  getCurrentAggregation,
  getOverridesWithNewAdded,
  getUpdatedDynamicSubTitleKeys,
  getUpdatedSubTitles,
} from "../helper";
import Checkbox from "../../../../../UI/Form/Checkbox/Checkbox";
import { Note } from "../../General/LimitRows/LimitRows";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export const FormulaItem = styled.div(
  ({ isParameterized }) => `
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  ${
    !isParameterized &&
    `
      & > div:first-of-type {
        width: 60%;
      }
      & > div:last-of-type {
        width: 35%;
      }
    `
  }
`
);

export default function FormulaColumn({
  selectedColumn,
  setChartState,
  setSelectedColumn,
  theme,
  overrides,
  navigationState,
  fields,
  subTitles,
  chart,
}) {
  const { column, isParameterized } = selectedColumn;

  // clear column from additional props
  const array = column
    .replace("ReverseColor::", "")
    .replace("ROUNDED::", "")
    .replace("BORDERED::", "")
    .split("::");

  const label = array[array.length - 1];
  const [header, setHeader] = useState(label);
  const [fn, operator, one = "", two = "", three = "", four = ""] =
    array.filter((_, i) => i < array.length - 1);

  const generalType = formulaOptions.find((fo) =>
    fo.options.find((o) => o.value === operator)
  );

  const option = generalType?.options.find((o) => o.value === operator) ?? {};
  const { count } = option;

  const values = [one, two, three, four].filter((_, i) => i < count);

  const updateChartState = useCallback(
    (val, newOverrides = overrides) => {
      setChartState({
        ...chart,
        overrides: newOverrides,
        ...(chart.subTitles && {
          subTitles: getUpdatedSubTitles(chart, selectedColumn, val),
        }),
        coloredColumns: chart.coloredColumns?.map((cc) =>
          cc === column ? val : cc
        ),
        ...(chart.dynamicSubTitleKeys && {
          dynamicSubTitleKeys: getUpdatedDynamicSubTitleKeys(
            chart,
            column,
            val
          ),
        }),
      });

      setSelectedColumn({ ...selectedColumn, column: val });
    },
    [overrides, setChartState, chart, setSelectedColumn, selectedColumn, column]
  );

  const debounced = useMemo(
    () => debounce((val) => updateChartState(val), 500),
    [updateChartState]
  );

  function onHeaderChange(e) {
    const coppied = [...array];
    coppied[coppied.length - 1] = e.target.value;
    const val = coppied.join("::");
    setHeader(e.target.value);

    if (navigationState === "Preview") {
      debounced(val);
      return;
    }
    updateChartState(val);
  }

  function changeFormula(option) {
    updateChartState(
      [
        fn,
        option.value,
        ...values.filter((_, i) => i < option.count),
        label,
      ].join("::")
    );
  }

  function replaceExistingColumn(prev, newColumn) {
    return column.replace(prev, newColumn);
  }

  function addNewColumn(newColumn, index) {
    const newValues = values
      .map((col, i) => (i === index ? newColumn : col))
      .filter((col) => col);

    return [fn, operator, ...newValues, label].join("::");
  }

  function changeColumnSource(option, prev, index) {
    const aggregation = getCurrentAggregation(prev)?.prefix;
    const aggregaedColumn = option.ops?.alias ?? aggregation + option.name;

    const updatedOverrides = getOverridesWithNewAdded(
      overrides,
      option,
      subTitles,
      prev
    );

    const val = prev
      ? replaceExistingColumn(prev, aggregaedColumn)
      : addNewColumn(aggregaedColumn, index);

    updateChartState(val, updatedOverrides);
  }

  function changeColumnAggregation(option, prev) {
    // set new aggregation instead of old one
    const newAggregated = option.prefix + getRidOfAggregation(prev);
    updateChartState(column.replace(`::${prev}::`, `::${newAggregated}::`));
  }

  const dynamicLabelLetter = ["A", "B", "C", "D"];

  // if user manually added not all columns from formula checkbox should be unchecked
  function includeAnyInNoTotal() {
    return (chart.noTotalCalcKeys ?? []).some((key) => values.includes(key));
  }

  // to make it work for formula all columns which we using in formula should be in this array
  function includeAllInNoTotal() {
    return values.every((value) =>
      (chart.noTotalCalcKeys ?? []).includes(value)
    );
  }

  /**
   * Updates the 'noTotalCalcKeys' in the 'chart' state based on whether all values
   * should be included or excluded from the calculation.
   */
  const excludeFromTotals = () => {
    // Check if all values should be included
    const includeAll = includeAllInNoTotal();

    // Determine the updated 'noTotalCalcKeys' based on inclusion or exclusion
    const updatedKeys = includeAll
      ? chart.noTotalCalcKeys.filter((key) => !values.includes(key))
      : [...(chart.noTotalCalcKeys ?? []), ...values];

    // Update the 'chart' state with the new 'noTotalCalcKeys' array
    setChartState({
      ...chart,
      noTotalCalcKeys: [...new Set(updatedKeys)],
    });
  };

  const partialInclude = !includeAllInNoTotal() && includeAnyInNoTotal();

  return (
    <div>
      <Select
        options={formulaOptions}
        value={option}
        onChange={changeFormula}
      />
      <br />
      <hr color={theme.background.secondary} />
      <br />

      {values.map((col, i) => (
        <FormulaItem key={col + i} isParameterized={isParameterized}>
          <Select
            options={fields}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.name}
            value={getColumn(overrides, col)}
            onChange={(o) => changeColumnSource(o, col, i)}
            label={`Value ${dynamicLabelLetter[i]}`}
          />
          {!isParameterized && (
            <Select
              options={[{ label: "None", prefix: "" }, ...aggregations]}
              value={getCurrentAggregation(col)}
              label="Aggregate"
              onChange={(o) => changeColumnAggregation(o, col)}
              isDisabled={!getColumn(overrides, col)}
            />
          )}
        </FormulaItem>
      ))}

      <hr color={theme.background.secondary} style={{ margin: "10px 0px" }} />

      <Input
        label="Column Header Label"
        inPlaceEdit
        value={header || label}
        hideEditIcon
        onChange={onHeaderChange}
      />

      <hr color={theme.background.secondary} style={{ margin: "10px 0px" }} />
      <Checkbox
        label="Exclude from Totals"
        checked={includeAllInNoTotal()}
        onChange={excludeFromTotals}
      />

      {partialInclude && (
        <Note color="warningDark">
          Some columns from formula are not in 'Exclude from Totals' list.
          <FontAwesomeIcon icon={["fas", "exclamation-triangle"]} />
        </Note>
      )}
    </div>
  );
}
